import React, { useState, useEffect, useRef } from 'react';
import SearchBar from './SearchBar';
import { useIsMobile } from '../hooks/useIsMobile';
import ConfirmModal from './ConfirmModal';
import IngredientFormModal from './IngredientFormModal';
import { IngredientCatalog } from '../models/diary';
import { ingredientService, CreateIngredientData } from '../services/ingredientService';

const fmt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : String(v);

const IngredientCatalogList: React.FC = () => {
  const [items, setItems] = useState<IngredientCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editItem, setEditItem] = useState<IngredientCatalog | undefined>(undefined);
  const [deleteItem, setDeleteItem] = useState<IngredientCatalog | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.values(menuRefs.current).every(
        ref => !ref || !ref.contains(event.target as Node)
      );
      if (clickedOutside) setActiveMenu(null);
    };
    if (activeMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenu]);

  const fetchItems = async (page: number = 1, searchQuery: string = search) => {
    try {
      setLoading(true);
      const response = await ingredientService.getAll(page, 10, searchQuery);
      setItems(response.results);
      setCurrentPage(response.actualPage);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to fetch ingredients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchItems(1, search);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCreate = async (data: Partial<CreateIngredientData>) => {
    await ingredientService.create(data as CreateIngredientData);
    setShowCreateModal(false);
    await fetchItems(1);
    showSuccess('Ingrediente creado correctamente.');
  };

  const handleEdit = async (data: Partial<CreateIngredientData>) => {
    if (!editItem) return;
    await ingredientService.update(editItem.id, data);
    setEditItem(undefined);
    await fetchItems(currentPage);
    showSuccess('Ingrediente actualizado correctamente.');
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await ingredientService.remove(deleteItem.id);
      setDeleteItem(null);
      setDeleteError('');
      await fetchItems(currentPage);
      showSuccess('Ingrediente eliminado correctamente.');
    } catch (error: any) {
      const message = error.response?.error || error.message || 'Error al eliminar el ingrediente.';
      setDeleteError(message);
      console.error('Failed to delete ingredient:', error);
    }
  };

  const thStyle: React.CSSProperties = { padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 13 };
  const thNum: React.CSSProperties = { ...thStyle, textAlign: 'right' };
  const tdNum: React.CSSProperties = { padding: '14px 16px', textAlign: 'right', color: '#6b7280', fontSize: 14 };

  return (
    <div>
      {successMessage && (
        <div style={{
          background: '#dcfce7', color: '#15803d', padding: '12px 16px', borderRadius: 8,
          marginBottom: 16, fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>&#10003;</span>
          {successMessage}
        </div>
      )}
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: isMobile ? 12 : 0, marginBottom: 24,
      }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por nombre" fullWidth={isMobile} />
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)', transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
          <span>Nuevo Ingrediente</span>
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>Cargando ingredientes...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>No se encontraron ingredientes</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={thStyle}>NOMBRE</th>
                <th style={thNum}>CAL</th>
                <th style={thNum}>PROT</th>
                <th style={thNum}>CARB</th>
                <th style={thNum}>GRAS</th>
                {!isMobile && <th style={thNum}>FIBRA</th>}
                {!isMobile && <th style={thNum}>AGUA</th>}
                <th style={{ ...thStyle, textAlign: 'center' }}>ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr
                  key={item.id}
                  style={{ borderBottom: index < items.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1f2937', fontSize: 15 }}>{item.name}</td>
                  <td style={tdNum}>{fmt(item.calories)}</td>
                  <td style={tdNum}>{fmt(item.proteins)}</td>
                  <td style={tdNum}>{fmt(item.carbohydrates)}</td>
                  <td style={tdNum}>{fmt(item.fats)}</td>
                  {!isMobile && <td style={tdNum}>{fmt(item.fiber)}</td>}
                  {!isMobile && <td style={tdNum}>{fmt(item.water)}</td>}
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <div ref={(el) => { menuRefs.current[item.id] = el; }} style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === item.id ? null : item.id); }}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px',
                          fontSize: '24px', color: '#555', lineHeight: 1, borderRadius: '50%',
                          width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center',
                          justifyContent: 'center', transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f0f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        &#8942;
                      </button>
                      {activeMenu === item.id && (
                        <div style={{
                          position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: '#fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '8px', minWidth: '180px',
                          padding: '8px 0', zIndex: 100,
                        }}>
                          <button
                            style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#222' }}
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setEditItem(item); }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            Editar
                          </button>
                          <button
                            style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '15px', color: '#e53e3e' }}
                            onClick={(e) => { e.stopPropagation(); setActiveMenu(null); setDeleteItem(item); }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#fff5f5')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && items.length > 0 && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '24px', padding: '16px 0' }}>
          <button
            onClick={() => { const p = currentPage - 1; setCurrentPage(p); fetchItems(p, search); }}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
              background: currentPage === 1 ? '#f8fafc' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#1f2937',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '14px',
            }}
          >
            Anterior
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const isCurrentPage = page === currentPage;
              const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
              if (!showPage) {
                if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>;
                }
                return null;
              }
              return (
                <button
                  key={page}
                  onClick={() => { setCurrentPage(page); fetchItems(page, search); }}
                  style={{
                    padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0',
                    background: isCurrentPage ? '#38b2ac' : '#fff', color: isCurrentPage ? '#fff' : '#1f2937',
                    cursor: 'pointer', fontWeight: isCurrentPage ? 600 : 500, fontSize: '14px', minWidth: '36px',
                  }}
                >
                  {page}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { const p = currentPage + 1; setCurrentPage(p); fetchItems(p, search); }}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
              background: currentPage === totalPages ? '#f8fafc' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#1f2937',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '14px',
            }}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <IngredientFormModal
        open={showCreateModal || !!editItem}
        item={editItem}
        onSave={editItem ? handleEdit : handleCreate}
        onCancel={() => { setShowCreateModal(false); setEditItem(undefined); }}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        open={!!deleteItem}
        title="Eliminar ingrediente"
        message={
          <div>
            <span>{`¿Estás seguro de que deseas eliminar "${deleteItem?.name}"? Esta acción no se puede deshacer.`}</span>
            {deleteError && (
              <div style={{ marginTop: 12, background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 6, fontSize: 14 }}>
                {deleteError}
              </div>
            )}
          </div>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => { setDeleteItem(null); setDeleteError(''); }}
      />
    </div>
  );
};

export default IngredientCatalogList;
